import React, { useState, useEffect, useReducer } from "react";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import moment from "moment";

import { readDocument, updateDocument } from "@/lib/db";
import { useAuth } from "@/lib/auth";
import SignUpSignIn from "@/components/user/SignUpSignIn";
import Button from "@/ui/Button";
import TextField from "@/ui/TextField";

import HomeHeader from "@/components/home/HomeHeader";

import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import { imageLoader } from "@/utils/image";

const defaultFinancialCalculator = [
    {
        index: 1,
        section: "Do you receive Social Security income?",
        question: "If yes, what is your monthly Social Security income?",
        answer: 0,
        label: "Monthly Social Security income",
    },
    {
        index: 2,
        section: "Do you have retirement savings, such as a 401K or IRA?",
        question:
            "What is the total amount you plan to withdraw from all retirement savings monthly?",
        answer: 0,
        label: "Monthly withdrawals from Retirement savings, such as a 401K or IRA, income",
    },
    {
        index: 3,
        section:
            "Do you have personal savings, such as cash, money market, stocks, CDs, home sale proceeds, etc?",
        question:
            "If you plan to make monthly withdrawals, what is the total monthly income from all personal savings?",
        answer: 0,
        label: "Monthly withdrawals from personal savings",
    },
    {
        index: 4,
        section:
            "Will you receive outside financial support? For example, will adult children or family members contribute to your living expenses?",
        question:
            "If you will receive outside financial support, what is the total monthly amount to be received?",
        answer: 0,
        label: "Additional monthly financial support",
    },
    {
        index: 5,
        section:
            "Do you have any other sources of monthly income, such base property income, business entities, reverse mortgage, etc?",
        question: "What is the total amount of monthly income?",
        answer: 0,
        label: "Additional sources of monthly income",
    },
    {
        index: 6,
        section: "Do you have annuity or pension incomes?",
        question:
            "What is your total monthly income from all other annuities and pensions?",
        answer: 0,
        label: "Monthly annuity or pension incomes",
    },
    {
        index: 7,
        section: "Do you have a Long-Term Care Insurance policy?",
        question: "What is your monthly Long-Term Care income?",
        answer: 0,
        label: "Monthly Long-Term Care Insurance funds",
    },
    {
        index: 8,
        section: "Do you receive Medicare assistance?",
        question: "What is your monthly Medicare assistance?",
        answer: 0,
        label: "Monthly Medicare assistance",
    },
    {
        index: 9,
        section: "Do you receive Medicaid (or Medi-Cal) assistance?",
        question: "What is your monthly Medicaid (Medi-Cal) assistance?",
        answer: 0,
        label: "Monthly Medicaid (or Medi-Cal) assistance",
    },
    {
        index: 10,
        section: "Do you have a veteran pension?",
        question: "What is your monthly veteran pension income?",
        answer: 0,
        label: "Monthly veteran pension income",
    },
    {
        index: 11,
        section: "Do you receive Veteran Aid & Attendance?",
        question: "What is the amount you receive monthly from Veteran Aid & Attendance?",
        answer: 0,
        label: "Monthly Veteran Aid & Attendance benefits",
    },
    {
        index: 12,
        section: "Do you receive any other monthly income or benefits?",
        question: "What is the amount you receive monthly from other sources?",
        answer: 0,
        label: "Other monthly income or benefits",
    },
    {
        index: 13,
        section: "Do you have a home equity loan or bridge loan?",
        question: "What is the monthly payment for your home equity or bridge loan?",
        label: "Home equity and bridge loans",
        answer: 0,
    },
    {
        index: 14,
        section: "Do you have a life insurance loan?",
        question: "What is the monthly payment for your life insurance loan?",
        answer: 0,
        label: "Life Insurance Loan",
    },
];

const getTotal = (sections) => {
    let total = 0;

    if (sections) {
        Object.values(sections).forEach((section) => {
            total += parseInt(section.answer);
        });
    }

    return total;
};

const updateUser = async (user, sections, submittedFinancialCalculator) => {
    if (user.uid) {
        await updateDocument("users", user.uid, {
            ...user,
            financialCalculator: sections,
            submittedFinancialCalculator: submittedFinancialCalculator
                ? submittedFinancialCalculator
                : "",
        });
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case "LOADING":
            return { ...state, loading: true };

        case "INIT":
            if (action.user.financialCalculator) {
                return { ...state, loading: false, user: action.user };
            } else {
                return {
                    ...state,
                    loading: false,
                    user: {
                        ...action.user,
                        financialCalculator: defaultFinancialCalculator,
                    },
                };
            }

        case "UPDATE": {
            const sections = JSON.parse(JSON.stringify(state.user.financialCalculator));
            const section = sections.find((s) => s.section === action.section);
            if (section) {
                section.answer = action.answer;
            }

            updateUser(state.user, sections, state.user.submittedFinancialCalculator);

            return { ...state, user: { ...state.user, financialCalculator: sections } };
        }

        case "SUBMIT":
            updateUser(
                state.user,
                state.user.financialCalculator,
                action.submit ? moment().format("MM/DD/YYYY") : ""
            );

            return {
                ...state,
                user: {
                    ...state.user,
                    submittedFinancialCalculator: action.submit
                        ? moment().format("MM/DD/YYYY")
                        : "",
                },
            };

        case "RESET":
            return {
                ...state,
                user: {
                    ...state.user,
                    financialCalculator: defaultFinancialCalculator,
                    submittedFinancialCalculator: "",
                },
            };

        default:
            return state;
    }
};

const FinancialCalculator = () => {
    const router = useRouter();
    const auth = useAuth();
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openSignIn, setOpenSignIn] = useState(false);
    const [state, dispatch] = useReducer(reducer, {
        loading: true,
        user: {
            submittedFinancialCalculator: "",
            financialCalculator: defaultFinancialCalculator,
        },
    });

    const userId = auth.user ? auth.user.uid : "";

    useEffect(() => {
        const init = async (uid) => {
            dispatch({ type: "LOADING" });

            const user = await readDocument("users", uid);
            if (user) {
                dispatch({ type: "INIT", user });
            }
        };

        if (userId) {
            init(userId);
        }
    }, [userId]);

    const handleChange = (section, question, answer) => {
        dispatch({
            type: "UPDATE",
            section,
            question,
            answer,
        });
    };

    const handleSubmit = () => {
        dispatch({ type: "SUBMIT", submit: true });
        window.scrollTo(0, 0);
    };

    const handleDownloadPdf = () => {
        router.push("/resources/financial-calculator-pdf");
        window.scrollTo(0, 0);
    };

    const handleUpdate = () => {
        dispatch({ type: "SUBMIT", submit: false });
        window.scrollTo(0, 0);
    };

    const handleReset = () => {
        if (window.confirm("Clear all selections?")) {
            dispatch({ type: "RESET" });
            window.scrollTo(0, 0);
        }
    };

    console.log("state", state);

    return (
        <div className="bg-gray-50">
            <HomeHeader fixed />
            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        {
                            name: "Financial Calculator",
                            href: "/resources/living-preferences",
                        },
                    ]}
                />
            </div>
            <div>
                <div className="hidden sm:block">
                    <div className="relative w-full h-40 sm:h-72 bg-black">
                        <Image
                            loader={imageLoader}
                            src="https://cdn.purpledoorfinders.com/purpledoorfinders/right-fit-tools"
                            alt="purple-door-finders-articles-for-memory-care"
                            layout="fill"
                            objectFit="cover"
                            objectPosition="0px -200px"
                            priority
                            unoptimized={false}
                            className="opacity-70"
                        />

                        <div className="flex justify-center">
                            <h1 className="absolute px-8 top-12 sm:top-32 text-3xl sm:text-5xl text-white font-headline font-bold">
                                Assisted Living Financial Calculator
                            </h1>
                        </div>
                    </div>
                </div>

                <div className="sm:hidden">
                    <h1 className="px-4 text-3xl text-primary-500 font-headline font-bold">
                        Assisted Living Financial Calculator
                    </h1>
                </div>

                {userId === "" ? (
                    <div className="container mx-auto mt-4 py-8 px-4 sm:px-16 bg-gray-300 rounded-lg">
                        <h4 className="font-bold text-2xl">
                            You need to be registed and signed in to use this tool and save
                            the information.
                        </h4>

                        <div className="mt-4 flex items-center">
                            <Button
                                className=""
                                onClick={() => {
                                    setOpenSignUp(true);

                                    gtag("event", "conversion", {
                                        send_to: "AW-10778046073/z0kvCMDQspMaEPncr5Mo",
                                    });

                                    oaiq("measure", "registration_completed", {
                                        type: "customer_action",
                                    });
                                }}
                            >
                                Create Account
                            </Button>
                        </div>

                        <div className="mt-4">
                            Already have an account?
                            <buton
                                className="ml-4 text-purple-500 hover:underline"
                                onClick={() => setOpenSignIn(true)}
                            >
                                Sign In
                            </buton>
                        </div>
                    </div>
                ) : null}

                {(userId ? !state.loading : true) &&
                !state.user.submittedFinancialCalculator ? (
                    <div className="container mx-auto py-12 px-4 sm:px-16">
                        <div>
                            <h2 className="text-3xl text-primary-500 font-bold font-headline">
                                Get Started
                            </h2>

                            <p className="mt-8">
                                The Assisted Living financial calculator will guide you
                                through the ways of affording senior and assisted living. If
                                you are completing this for more than one resident, please
                                include the total amount of income for both residents who
                                will be residing in a single unit.
                            </p>

                            <p className="mt-4">
                                Answer each question to the best of your ability. Upon
                                completion of the calculator you can download your budget,
                                save to your profile to access later and share, and
                                download.
                            </p>

                            <hr className="my-8 border-black" />

                            <div className="flex flex-col space-y-8">
                                {state.user.financialCalculator.map((section) => (
                                    <Section
                                        key={section.index}
                                        section={section}
                                        onChange={handleChange}
                                    />
                                ))}
                            </div>

                            <div className="mt-8 flex justify-end">
                                <Button
                                    className="uppercase"
                                    onClick={handleSubmit}
                                    disabled={!userId}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}

                {userId && !state.loading && state.user.submittedFinancialCalculator ? (
                    <div className="container mx-auto py-12 px-4 sm:px-16">
                        <div>
                            <h2 className="text-3xl text-primary-500 font-bold font-headline">
                                You're All Done!
                            </h2>

                            <p className="mt-8">
                                Your personal Assisted Living Financial Calculator is
                                complete. You now have your budget that includes your
                                financial resources to afford senior and assisted living.
                            </p>

                            <hr className="my-8 border-black" />

                            <div className="mt-2 flex flex-col space-y-4 max-w-md">
                                {state.user.financialCalculator.map((section) => (
                                    <div
                                        key={section.section}
                                        className="flex justify-between space-x-16"
                                    >
                                        <p className="font-black">{section.label}</p>
                                        <div>${section.answer}</div>
                                    </div>
                                ))}

                                <hr className="my-8 border-black" />

                                <div className="flex justify-between space-x-16">
                                    <p className="font-black">Total Monthly Income</p>
                                    <div>${getTotal(state.user.financialCalculator)}</div>
                                </div>

                                <div className="flex justify-between space-x-16">
                                    <p className="font-black">Total Monthly Budget*</p>
                                    <div>
                                        $
                                        {Math.round(
                                            0.8 * getTotal(state.user.financialCalculator)
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 p-4 border-2 border-black">
                                <p>
                                    Purple Door Finders recommends planning on using 80% of
                                    your budgeted income to cover senior and assisted and
                                    care needs. We recommend this because base rates and
                                    care fees increase with the cost of living as does your
                                    level of care as you continue to age.
                                </p>

                                <p className="mt-4">
                                    This financial calculator is a planning tool and does
                                    not guarantee affordability of living needs. Always
                                    consult with your financial advisor. Your benefits and
                                    income from certain types of income sources may vary
                                    depending on the type of facility, their licensing, your
                                    level of care and other factors. Be sure to check with
                                    all your income sources to ensure the benefits you are
                                    budgeting cover the type of living and care you are
                                    choosing or need.
                                </p>
                            </div>

                            <div className="mt-8 flex justify-start space-x-2 sm:space-x-8">
                                <Button className="uppercase" onClick={handleDownloadPdf}>
                                    Download as PDF
                                </Button>

                                <Button className="uppercase" onClick={handleUpdate}>
                                    Update Selections
                                </Button>

                                <Button
                                    variant="outlined"
                                    className="uppercase"
                                    onClick={handleReset}
                                >
                                    Clear All Selections
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            <SeoLinks />
            <HomeFooter />

            <SignUpSignIn
                openSignUp={openSignUp}
                openSignIn={openSignIn}
                setOpenSignUp={setOpenSignUp}
                setOpenSignIn={setOpenSignIn}
            />
        </div>
    );
};

const Section = ({ section, onChange }) => {
    return (
        <div className="">
            <h3 className="font-black">
                {section.index}. {section.section}
            </h3>

            <div className="mt-2 flex items-center space-x-4">
                <p>{section.question}</p>
                <TextField
                    type="number"
                    name={section.question}
                    value={section.answer}
                    onChange={(e) =>
                        onChange(section.section, section.question, e.target.value)
                    }
                    inputClassName="py-1 w-32"
                />
            </div>
        </div>
    );
};

export default FinancialCalculator;
